import { useEffect, useState, useCallback } from 'react';
import {
    trackGA4Event,
    setLoginBeginCookie,
    setSocialRegistrationBeginCookie,
} from '@gumtree/shared/src/util/ga4-shared';
import jwtDecode from 'jwt-decode';
import { LOGIN_APP_DISPLAY_NAME, LOGIN_PLATFORM } from './constants';
import { getErrorMsg } from './utils/social-errors';
import { LoginErrorType, SocialLoginPayload, SubmitSocialLoginFunction } from './types';

declare global {
    interface Window {
        AppleID: {
            auth: {
                init: (config: AppleAuthConfig) => void;
                signIn: () => Promise<AppleSignInResponse>;
            };
        };
    }
}

interface AppleAuthConfig {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
}

interface AppleSignInResponse {
    authorization: {
        code: string;
        id_token: string;
        state?: string;
    };
    user?: {
        email: string;
        name?: {
            firstName: string;
            lastName: string;
        };
    };
}

interface AppleIdTokenPayload {
    iss: string;
    aud: string;
    exp: number;
    iat: number;
    sub: string;
    email?: string;
    email_verified?: string;
    is_private_email?: string;
    auth_time: number;
    nonce_supported: boolean;
}

const APPLE_SCRIPT_ID = 'apple-signin-sdk';
const appleClientId = 'com.gumtreeuk2.iphone.web';

const useAppleSignIn = (submitLogin: SubmitSocialLoginFunction, type: 'login' | 'register') => {
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);

    const handleErrorsMessage = useCallback(
        (error: string) => {
            trackGA4Event(
                type === 'login'
                    ? {
                          event: 'user_login',
                          formName: 'user login',
                          formStep: 'user login',
                          formValidation: 'failure',
                          formError: error,
                          userAccountType: 'apple',
                      }
                    : {
                          event: 'user_registration',
                          formName: 'user registration',
                          formStep: 'user registration',
                          formValidation: 'failure',
                          formError: error,
                          userAccountType: 'apple',
                      }
            );

            setErrorMessage(error);
        },
        [type]
    );
    
    // 轮询检查 window.AppleID 是否准备好（类似 fbAsyncInit 的机制）
    const waitForAppleID = useCallback((callback: () => void, maxAttempts = 50) => {
        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            
            if (window.AppleID) {
                clearInterval(checkInterval);
                callback();
            } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
            }
        }, 100);
    }, []);
    
    const initAppleSignIn = useCallback(() => {
        if (window.AppleID && appleClientId) {
            window.AppleID.auth.init({
                clientId: appleClientId,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });
            setIsLoaded(true);
        }
    }, [appleClientId]);

    const loadAppleSdk = useCallback(() => {
        if (document.getElementById(APPLE_SCRIPT_ID)) {
            // Script already exists - check if AppleID is ready
            if (window.AppleID) {
                initAppleSignIn();
            } else {
                // Wait for AppleID to be ready
                waitForAppleID(initAppleSignIn);
            }
            return;
        }

        const script = document.createElement('script');
        script.id = APPLE_SCRIPT_ID;
        script.src =
            'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = () => {
            // 脚本加载完成，但 window.AppleID 可能还需要一点时间
            waitForAppleID(initAppleSignIn);
        };
        script.onerror = () => {
            console.error('Failed to load Apple Sign In SDK');
        };
        document.head.appendChild(script);
    }, [initAppleSignIn, waitForAppleID]);

    const onLaunch = async () => {
        if (!isLoaded || !window.AppleID) {
            const error = getErrorMsg({ appDisplayName: LOGIN_APP_DISPLAY_NAME.APPLE });
            handleErrorsMessage(error);
            return;
        }

        try {
            const response: AppleSignInResponse = await window.AppleID.auth.signIn();

            const { authorization, user } = response;
            const { id_token: idToken } = authorization;

            // Apple only returns user info on first authorization
            // Subsequent logins need to parse email from id_token
            let email = user?.email;

            if (!email && idToken) {
                const { email: decodedEmail } = jwtDecode<AppleIdTokenPayload>(idToken);
                email = decodedEmail;
            }

            if (!email) {
                const error = getErrorMsg({
                    errorType: LoginErrorType.ShareEmailError,
                    appDisplayName: LOGIN_APP_DISPLAY_NAME.APPLE,
                });
                handleErrorsMessage(error);
                return;
            }

            if (type === 'login') {
                setLoginBeginCookie('apple');
            }
            if (type === 'register') {
                setSocialRegistrationBeginCookie('apple');
            }

            // 构造Apple Token JSON对象
            const appleTokenJson = {
                firstName: user?.name?.firstName || null,
                lastName: user?.name?.lastName || null,
                idToken: idToken,
            };

            const loginPayload: SocialLoginPayload = {
                username: email,
                password: JSON.stringify(appleTokenJson),
                loginPlatform: LOGIN_PLATFORM.APPLE,
            };

            submitLogin(loginPayload);
        } catch (error: unknown) {
            // User cancelled the sign-in
            if (
                error &&
                typeof error === 'object' &&
                'error' in error &&
                (error as { error: string }).error === 'popup_closed_by_user'
            ) {
                return;
            }

            const errorMsg = getErrorMsg({ appDisplayName: LOGIN_APP_DISPLAY_NAME.APPLE });
            handleErrorsMessage(errorMsg);
        }
    };

    useEffect(() => {
        if (typeof document !== 'undefined' && appleClientId) {
            loadAppleSdk();
        }
    }, [appleClientId, loadAppleSdk]);

    return { onLaunch, errorMessage, isLoaded };
};

export default useAppleSignIn;
